/**
 * Location Service
 * Handles API calls for geographic data (States and Districts)
 */

import { env } from '../config/env';
import { authService } from './authService';

const BASE_URL = env.locationServiceBaseUrl;

/**
 * Fetch helper with conditional authentication
 * Uses authService when auth is enabled, otherwise makes unauthenticated requests
 */
async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const shouldUseAuth = env.authEnabled || env.authorizationEnabled;

  if (shouldUseAuth) {
    return await authService.authenticatedFetch(url, options);
  }
  
  return await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
}

/**
 * COMP-1762: shared fetch+parse+error-log helper for the three id+name lookup
 * endpoints (`fetchDistrictsLookup`, `fetchStatesLookup`,
 * `fetchDispatchAreasLookup`). Extracted after SonarQube flagged the
 * per-method boilerplate as duplicated code (~30 lines across the trio).
 * Callers pass the URL + init + a short label used only in the error message,
 * and do their own tiny map on the parsed body if they need to rename fields.
 */
async function fetchLookupJson<T>(
  url: string,
  init: RequestInit,
  errorLabel: string
): Promise<T> {
  try {
    const response = await fetchWithAuth(url, {
      headers: { 'Content-Type': 'application/json' },
      ...init,
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch ${errorLabel}: ${response.statusText}`);
    }
    return (await response.json()) as T;
  } catch (error) {
    console.error(`Error fetching ${errorLabel}:`, error);
    throw error;
  }
}

/**
 * State/District API Response
 */
export interface DistrictApiResponse {
  districtId: string;
  parentDistrictId: string;
  name: string;
  levelCode: string;
  stateCode?: string;
  districtType: string;
  hierarchyLevel: number;
  isActive: boolean;
  createdDate: string;
  modifiedDate: string;
  modifiedByUser: string;
}

/**
 * Transformed State for UI
 */
export interface State {
  id: string;
  name: string;
  stateCode: string;
  isActive: boolean;
}

/**
 * Transformed District for UI
 */
export interface District {
  id: string;
  name: string;
  stateId: string;
  isActive: boolean;
  /** Populated only by endpoints that return districts in a dispatch-area context. */
  dispatchAreaId?: string;
}

/**
 * DispatchArea API response (matches backend DispatchAreaDto).
 */
export interface DispatchAreaApiResponse {
  id: string;
  name: string;
  stateId: string;
  isActive: boolean;
}

/**
 * Transformed DispatchArea for UI
 */
export interface DispatchArea {
  id: string;
  name: string;
  stateId: string;
  isActive: boolean;
}

/**
 * Locations-by-ids API item (states + districts share this shape)
 */
export interface LocationLookupItem {
  districtId: string;
  name: string;
  stateCode?: string;
  parentDistrictId?: string;
  isActive: boolean;
}

/**
 * Locations-by-ids API response — server pre-splits states and districts
 */
export interface LocationsByIdsResponse {
  states: LocationLookupItem[];
  districts: LocationLookupItem[];
}

/**
 * Location Service
 */
export const locationService = {
  /**
   * Fetch all states
   */
  async fetchStates(includeInactive: boolean = false): Promise<State[]> {
    try {
      const response = await fetchWithAuth(`${BASE_URL}/Districts/states?includeInactive=${includeInactive}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch states: ${response.statusText}`);
      }

      const data: DistrictApiResponse[] = await response.json();

      return data.map(item => ({
        id: item.districtId,
        name: item.name,
        stateCode: item.stateCode ?? '',
        isActive: item.isActive
      }));
    } catch (error) {
      console.error('Error fetching states:', error);
      throw error;
    }
  },

  /**
   * Fetch districts for a specific state
   */
  async fetchDistrictsByState(stateId: string): Promise<District[]> {
    try {
      const response = await fetchWithAuth(`${BASE_URL}/Districts/states/${stateId}/school-districts`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch districts for state ${stateId}: ${response.statusText}`);
      }

      const data: DistrictApiResponse[] = await response.json();

      return data.map(item => ({
        id: item.districtId,
        name: item.name,
        stateId: item.parentDistrictId,
        isActive: item.isActive
      }));
    } catch (error) {
      console.error(`Error fetching districts for state ${stateId}:`, error);
      throw error;
    }
  },

  /**
   * Fetch districts for multiple states (parallel requests)
   */
  async fetchDistrictsForStates(stateIds: string[]): Promise<District[]> {
    try {
      if (stateIds.length === 0) {
        return [];
      }

      const promises = stateIds.map(stateId => this.fetchDistrictsByState(stateId));
      const results = await Promise.all(promises);
      
      // Flatten and remove duplicates
      const allDistricts = results.flat();
      const uniqueDistricts = allDistricts.filter(
        (district, index, self) => 
          index === self.findIndex(d => d.id === district.id)
      );
      
      return uniqueDistricts;
    } catch (error) {
      console.error('Error fetching districts for multiple states:', error);
      throw error;
    }
  },

  /**
   * Fetch all school districts (for grid display mapping)
   */
  async fetchAllDistricts(includeInactive: boolean = false): Promise<District[]> {
    try {
      const response = await fetchWithAuth(`${BASE_URL}/Districts/school-districts?includeInactive=${includeInactive}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch all districts: ${response.statusText}`);
      }

      const data: DistrictApiResponse[] = await response.json();

      return data.map(item => ({
        id: item.districtId,
        name: item.name,
        stateId: item.parentDistrictId,
        isActive: item.isActive
      }));
    } catch (error) {
      console.error('Error fetching all districts:', error);
      throw error;
    }
  },

  /**
   * COMP-1762: id+name(+stateCode) lookup projection of the school-districts
   * catalog. Use this in place of `fetchAllDistricts` when the caller only
   * needs to resolve GUIDs to display names or state abbreviations (grid
   * display maps). Backend returns `DistrictLookupDto[]` with DistrictId +
   * Name + optional StateCode — ~75% smaller on the wire than the full
   * `DistrictDto`.
   *
   * Default `includeInactive=true` — lookups exist to resolve historical
   * assignments to now-deactivated districts, per COMP-1762's "Internal IDs
   * should never be displayed" requirement. Callers who need active only can
   * pass `false` explicitly.
   */
  async fetchDistrictsLookup(
    includeInactive: boolean = true
  ): Promise<Array<{ id: string; name: string; stateCode?: string }>> {
    const data = await fetchLookupJson<
      Array<{ districtId: string; name: string; stateCode?: string }>
    >(
      `${BASE_URL}/Districts/school-districts/lookup?includeInactive=${includeInactive}`,
      { method: 'GET' },
      'districts lookup'
    );
    // COMP-1762 (hotfix): use a conditional spread instead of direct
    // assignment. tsconfig has `exactOptionalPropertyTypes: true`, which
    // treats `stateCode?: string` as "may be absent, but never present-with-
    // undefined" — so assigning `item.stateCode` (which IS `string | undefined`)
    // fails the type check. Spread only includes the property when the
    // source is defined.
    return data.map((item) => ({
      id: item.districtId,
      name: item.name,
      ...(item.stateCode !== undefined ? { stateCode: item.stateCode } : {}),
    }));
  },

  /**
   * COMP-1762: id+name(+stateCode) lookup projection of the states catalog.
   * Companion to `fetchDistrictsLookup` — used by callers (grid display maps)
   * that don't need `externalId` / `hierarchyLevel` / etc. from the full
   * `DistrictDto`. `stateCode` IS surfaced for future consumers that want to
   * show / group by state abbreviation.
   *
   * Default `includeInactive=true` — same rationale as the districts lookup.
   */
  async fetchStatesLookup(
    includeInactive: boolean = true
  ): Promise<Array<{ id: string; name: string; stateCode?: string }>> {
    const data = await fetchLookupJson<
      Array<{ districtId: string; name: string; stateCode?: string }>
    >(
      `${BASE_URL}/Districts/states/lookup?includeInactive=${includeInactive}`,
      { method: 'GET' },
      'states lookup'
    );
    // COMP-1762 (hotfix): same conditional-spread pattern as
    // fetchDistrictsLookup above — required by `exactOptionalPropertyTypes`.
    return data.map((item) => ({
      id: item.districtId,
      name: item.name,
      ...(item.stateCode !== undefined ? { stateCode: item.stateCode } : {}),
    }));
  },

  /**
   * COMP-1762: id+name lookup projection of dispatch areas belonging to the
   * supplied states. Companion to `fetchDistrictsLookup` / `fetchStatesLookup`
   * — used by grid display maps that don't need `stateId` / `isActive` from
   * the full `DispatchAreaDto`. Dialog pickers and cascade logic keep using
   * `fetchDispatchAreasByStateIds` (they still need `stateId`).
   *
   * Default `includeInactive=true` — same rationale as the other two lookups.
   * Sends the flag in the POST body; a `null`/omitted field on the backend
   * side would default to `true` too, but we explicitly pass the boolean so
   * network logs and swagger both show the intent.
   */
  async fetchDispatchAreasLookup(
    stateIds: string[],
    includeInactive: boolean = true
  ): Promise<Array<{ id: string; name: string }>> {
    if (stateIds.length === 0) return [];
    return fetchLookupJson<Array<{ id: string; name: string }>>(
      `${BASE_URL}/dispatch-areas/search/lookup`,
      { method: 'POST', body: JSON.stringify({ stateIds, includeInactive }) },
      'dispatch areas lookup'
    );
  },

  /**
   * Fetch states and districts in one bulk call by their IDs.
   * Used to resolve a non-admin user's assigned areas (authorizedStates / authorizedDistricts)
   * into displayable {id, name} pairs without pulling the full state/district catalogs.
   */
  async fetchLocationsByIds(
    stateIds: string[],
    districtIds: string[]
  ): Promise<{ states: State[]; districts: District[] }> {
    if (stateIds.length === 0 && districtIds.length === 0) {
      return { states: [], districts: [] };
    }

    const response = await fetchWithAuth(`${BASE_URL}/Districts/locations-by-ids`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stateIds, districtIds }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch locations by ids: ${response.statusText}`);
    }

    const data: LocationsByIdsResponse = await response.json();

    return {
      states: (data.states || []).map((item) => ({
        id: item.districtId,
        name: item.name,
        stateCode: item.stateCode ?? '',
        isActive: item.isActive,
      })),
      districts: (data.districts || []).map((item) => ({
        id: item.districtId,
        name: item.name,
        stateId: item.parentDistrictId || '',
        isActive: item.isActive,
      })),
    };
  },

  /**
   * Fetch dispatch areas for one or more states.
   * Calls POST /api/dispatch-areas/search with the id list in the request
   * body (COMP-1530). Previously this was GET ?stateIds=... but the URL
   * could exceed ~4K when large state-sets were supplied; the body shape
   * is unbounded.
   */
  async fetchDispatchAreasByStateIds(stateIds: string[]): Promise<DispatchArea[]> {
    if (stateIds.length === 0) return [];

    try {
      const response = await fetchWithAuth(`${BASE_URL}/dispatch-areas/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stateIds }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch dispatch areas: ${response.statusText}`);
      }

      const data: DispatchAreaApiResponse[] = await response.json();
      return data.map((item) => ({
        id: item.id,
        name: item.name,
        stateId: item.stateId,
        isActive: item.isActive,
      }));
    } catch (error) {
      console.error('Error fetching dispatch areas:', error);
      throw error;
    }
  },

  /**
   * Fetch school districts that belong to one or more dispatch areas.
   * Returned items carry the dispatch area they came from in `dispatchAreaId`.
   * Calls POST /api/districts/by-dispatch-areas/search with the id list in
   * the request body (COMP-1530). Previously this was GET
   * ?dispatchAreaIds=... but the URL could exceed ~4K when large DA-sets
   * were supplied; the body shape is unbounded.
   */
  async fetchDistrictsByDispatchAreaIds(dispatchAreaIds: string[]): Promise<District[]> {
    if (dispatchAreaIds.length === 0) return [];

    try {
      const response = await fetchWithAuth(`${BASE_URL}/Districts/by-dispatch-areas/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dispatchAreaIds }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch districts by dispatch areas: ${response.statusText}`);
      }

      const data: (DistrictApiResponse & { dispatchAreaId?: string })[] = await response.json();
      return data.map<District>((item) => {
        const base: District = {
          id: item.districtId,
          name: item.name,
          stateId: item.parentDistrictId,
          isActive: item.isActive,
        };
        if (item.dispatchAreaId) base.dispatchAreaId = item.dispatchAreaId;
        return base;
      });
    } catch (error) {
      console.error('Error fetching districts by dispatch areas:', error);
      throw error;
    }
  },

  /**
   * Create lookup maps for quick access
   */
  createStateLookupMap(states: State[]): Record<string, string> {
    return states.reduce((map, state) => {
      map[state.id] = state.name;
      return map;
    }, {} as Record<string, string>);
  },

  /**
   * Create district lookup map for quick access
   */
  createDistrictLookupMap(districts: District[]): Record<string, string> {
    return districts.reduce((map, district) => {
      map[district.id] = district.name;
      return map;
    }, {} as Record<string, string>);
  }
};
